import {memo, useMemo} from 'react';

import styled from 'styled-components';

import {Skeleton} from '~/shared/components/Loaders';
import {PriceLabel} from '~/shared/components/PriceLabels';
import {useIsMinDesktop, useIsMinLargeMobile} from '~/shared/hooks/deviceInfo';
import {getLocalizationService} from '~/shared/services/localisationService';
import {Link as _Link} from '~/shared/router';
import {mapDebitTransferStatus} from '~/shared/consts/userTransactionsConsts';
import {Title, lowerCaseFirstLetter} from '~/shared/components/UserTransactions/shared';
import CustomTable from '~/shared/components/UserTransactions/components/CustomTable/CustomTable';
import {
  Root,
  TableMobileWrapper,
  Column as CustomColumn,
  InnerRow,
  DateAndTime,
  ModifiedSkeletonLoading,
} from '~/shared/components/UserTransactions/components/styled';

import {Separator} from '../Shared';

const Link = styled(_Link)`
  color: ${({theme}) => theme.userReport.sendDebitTransferRequestModal.linkColor};
  text-decoration: underline;
`;
const mapTranslateRequestStatus = {
  [mapDebitTransferStatus.Approved]: 'waiting_for_approval',
  [mapDebitTransferStatus.Cancelled]: 'cancelled',
  [mapDebitTransferStatus.Completed]: 'request_completed',
  [mapDebitTransferStatus.Declined]: 'request_declined',
  [mapDebitTransferStatus.Expired]: 'request_is_expired',
  [mapDebitTransferStatus.Failed]: 'request_failed',
  [mapDebitTransferStatus.New]: 'waiting_for_approval',
  [mapDebitTransferStatus.Validated]: 'waiting_for_approval',
  [mapDebitTransferStatus.ValidationFailed]: 'request_failed',
};

const showCancelButtonForStatuses = [mapDebitTransferStatus.Validated, mapDebitTransferStatus.New];

const MobileDebitSentRequestsTable = ({transfers, onSentDebitTransferCancelClick, className}) => {
  const {t} = getLocalizationService();
  const isMinLargeMobile = useIsMinLargeMobile();

  return (
    <Root mobile mwSeparator className={className}>
      <Title isMobile>{t('debit_transfer_your_sent_requests')}</Title>
      <ModifiedSkeletonLoading shouldShowLoader={!transfers} noMarginTop>
        <TableMobileWrapper>
          {transfers.map(
            ({
              id,
              targetUserName,
              transferAmount,
              dateAndTime,
              restaurantName,
              debitTransferRequestStatusLabel,
              debitTransferRequestStatus,
              transferId,
            }) => {
              const shouldShowCancelButton = showCancelButtonForStatuses.includes(debitTransferRequestStatus);
              return (
                <CustomColumn data-test-id={`transferid_${transferId}`} key={id}>
                  <InnerRow bold>
                    <div>{targetUserName}</div>
                    <PriceLabel price={transferAmount} />
                  </InnerRow>
                  <InnerRow>
                    <DateAndTime>{dateAndTime}</DateAndTime>
                    <div>{restaurantName}</div>
                  </InnerRow>
                  <InnerRow>
                    {shouldShowCancelButton ? (
                      <Link onClick={() => onSentDebitTransferCancelClick(transferId)}>{t('cancel')}</Link>
                    ) : (
                      <div>{debitTransferRequestStatusLabel}</div>
                    )}
                  </InnerRow>
                </CustomColumn>
              );
            },
          )}
        </TableMobileWrapper>
      </ModifiedSkeletonLoading>
      {!isMinLargeMobile && <Separator />}
    </Root>
  );
};

const DebitSentRequestsTable = ({debitTransfersSentRequests, onSentDebitTransferCancelClick, tableHeaderCustomColor, className = ''}) => {
  const {t} = getLocalizationService();
  const isMinDesktop = useIsMinDesktop();

  const columns = [
    {
      name: 'emptyCell',
      hideLabel: true,
      width: 32,
    },
    {
      name: 'dateAndTime',
      label: t('date_and_time'),
      width: 171,
    },
    {
      name: 'restaurantName',
      label: t('business_name_2'),
      grow: true,
      width: 160,
    },
    {
      name: 'targetUserName',
      label: t('from_who_requested'),
      grow: true,
      width: 150,
    },
    {
      name: 'total',
      label: t('total'),
      width: 80,
    },
    {
      name: 'debitTransferRequestStatusLabel',
      label: t('request_status'),
      width: 100,
    },
    {
      name: 'cancelletion',
      label: t('cancel_request'),
      width: 80,
    },
  ];
  const transfers = useMemo(() => {
    if (!debitTransfersSentRequests) {
      return;
    }
    return (
      debitTransfersSentRequests &&
      debitTransfersSentRequests
        .map(transfer => ({
          ...transfer,
          id: transfer.transferId,
          dateAndTime: `${transfer.transferRequestDateStr} ${transfer.transferRequestTimeStr}`,
          debitTransferRequestStatus: lowerCaseFirstLetter(transfer.debitTransferRequestStatus),
          debitTransferRequestStatusLabel: t(
            mapTranslateRequestStatus[lowerCaseFirstLetter(transfer.debitTransferRequestStatus)],
          ),
          total: transfer.transferAmount,
        }))
        .reverse()
    );
  }, [debitTransfersSentRequests, t]);

  if (!debitTransfersSentRequests || debitTransfersSentRequests.length === 0) {
    return null;
  }
  if (!isMinDesktop) {
    return (
      <MobileDebitSentRequestsTable
        transfers={transfers}
        onSentDebitTransferCancelClick={onSentDebitTransferCancelClick}
        className={className}
        tableHeaderCustomColor={tableHeaderCustomColor}
      />
    );
  }
  return (
    <Root className={className}>
      <Title>{t('debit_transfer_your_sent_requests')}</Title>
      <CustomTable
        columns={columns}
        rows={transfers}
        boldAllPrices
        withRoundedCorners
        onCancelButtonClick={onSentDebitTransferCancelClick}
        skeletonComponent={<Skeleton width={88} height={16} />}
        debitSentRequestsTable
        tableHeaderCustomColor={tableHeaderCustomColor}
        customArtributeOptions={{
          getArtributeData: transferId => `transferid_${transferId}`,
          dataArg: 'transferId',
          artributeName: 'data-test-id',
        }}
      />
    </Root>
  );
};

export default memo(DebitSentRequestsTable);
